import React, { useEffect, useState } from "react";
import { useForm, FormProvider } from "react-hook-form";
import TextField from "@/src/components/custom-fields/TextField";
import Iconify from "@/src/components/iconify/Iconify";
import IconCode from "@/src/lib/iconCode";
import OtpInput from "react-otp-input";
import { headerApiService } from "@/src/lib/api-services/headerApiServices";
import { useToast } from "@/src/lib/hooks/useToast";
import useAuth from "@/src/lib/hooks/useAuth";
import { useRouter } from "next/router";
import { useHODMutation } from "@/src/lib/hooks/useHODMutation";
import CustomButton from "@/src/lib/ui/Button";
import CustomIconButton from "@/src/lib/ui/iconButton";
import ConfirmDialog from "@/src/lib/ui/confirmDialog";

const CallBackRequest = ({ open, onClose, currentTest, currentCentre }) => {
    const { isLoggedIn } = useAuth();
    const INITIAL_STEP = isLoggedIn ? 0 : 1;
    const router = useRouter();
    const { showToast, showCustomToast } = useToast();
    const [currentStep, setCurrentStep] = useState(INITIAL_STEP);
    const [mobileNumber, setMobileNumber] = useState("");
    const [otpRequestId, setOtpRequestId] = useState(null);
    const [otp, setOtp] = useState("");

    const methods = useForm();

    const { handleSubmit } = methods;

    const sentOtpMutation = useHODMutation(
        (data) => headerApiService.getOtpForUploadPrecsription(data),
        {
            onSuccess: (data) => {
                setOtpRequestId(data?.data?.data?.requestId);
                showToast(data);
                setCurrentStep(2);
            },
            onError: (error) => {
                showToast(error);
            },
        }
    );

    const { mutate: placeCallBackRequestMutation, isLoading } = useHODMutation(
        (data) => headerApiService.placeCallBackRequest(data),
        {
            onSuccess: (data) => {
                showToast(data);
                setCurrentStep(3);
                //handleClose();
            },
            onError: (error) => {
                showToast(error);
            },
        }
    );

    useEffect(() => {
        if (currentStep === 3) {
            const timer = setTimeout(() => {
                onClose();
                setCurrentStep(INITIAL_STEP);
            }, 3000);

            return () => clearTimeout(timer);
        }
    }, [currentStep, onClose]);

    // Handle mobile number change
    const handleMobileNumberChange = (e) => {
        setMobileNumber(e.target.value);
    };

    // Handle OTP submit
    const handleOtpSubmit = async () => {
        try {
            const payload = {
                centreName: currentCentre?.centre,
                pageUri: router.asPath,
                testName: currentTest?.itemName,
                ...(isLoggedIn
                    ? {}
                    : { mobileNo: mobileNumber, requestId: otpRequestId, otp }),
            };

            placeCallBackRequestMutation(payload);
        } catch (error) {
            console.log("ERROR:", error);
        }
    };

    const onSubmit = async () => {
        if (currentStep === 1) {
            if (!mobileNumber || mobileNumber.length !== 10) {
                alert("Please enter a valid 10-digit mobile number.");
                return;
            }

            sentOtpMutation.mutate({ mobileNo: mobileNumber });
        }
    };

    // Handle resending OTP
    const handleResendOtp = async () => {
        setOtp("");
        sentOtpMutation.mutate({ mobileNo: mobileNumber });
        showCustomToast("OTP has been resent.", "success");
    };

    // Handle editing mobile number
    const handleEditMobileNumber = () => {
        setOtp("");
        setCurrentStep(1);
    };

    return (
        <ConfirmDialog
            open={open}
            onClose={onClose}
            fullWidth
            maxWidth="md"
            title={
                <div className="border-b border-solid border-gray-200 -mx-4 px-4 pb-4 flex flex-row items-center justify-between">
                    <h3 className="md:text-xl text-lg font-bold">
                        Callback Request
                    </h3>

                    <CustomIconButton onClick={onClose}>
                        <Iconify icon={IconCode.close} size={24} />
                    </CustomIconButton>
                </div>
            }
            content={
                <div>
                    <FormProvider {...methods}>
                        <form onSubmit={handleSubmit(onSubmit)}>
                            <div className="flex flex-col md:gap-4 gap-2">
                                {currentStep === 0 && isLoggedIn && (
                                    <>
                                        <div>
                                            <h6 className="font-bold md:text-2xl text-xl">
                                                Are you sure you want to request
                                                a callback?
                                            </h6>
                                        </div>

                                        <div className="flex md:flex-row flex-col gap-2">
                                            <CustomButton
                                                onClick={onClose}
                                                className="md:w-1/2 w-full"
                                            >
                                                Close
                                            </CustomButton>
                                            <CustomButton
                                                onClick={handleOtpSubmit}
                                                variant="contained"
                                                color="primary"
                                                className="md:w-1/2 w-full"
                                                // endIcon={
                                                //     isLoading && (
                                                //         <Iconify
                                                //             icon={
                                                //                 IconCode.loadingTwoTone
                                                //             }
                                                //         />
                                                //     )
                                                // }
                                                disabled={isLoading}
                                            >
                                                confirm
                                            </CustomButton>
                                        </div>
                                    </>
                                )}
                                {currentStep === 1 && (
                                    <>
                                        <div>
                                            <h5 className="font-bold md:text-2xl text-xl">
                                                Enter Mobile Number
                                            </h5>
                                            <div></div>
                                            <h6 className="text-sm font-medium mt-1">
                                                An OTP will be sent to your
                                                mobile number.
                                            </h6>
                                        </div>
                                        <TextField
                                            fullWidth
                                            placeholder="Enter 10-digit mobile number"
                                            value={mobileNumber}
                                            onChange={handleMobileNumberChange}
                                            size="small"
                                        />

                                        <div className="flex md:flex-row flex-col gap-2">
                                            <CustomButton
                                                onClick={onClose}
                                                className="md:w-1/2 w-full"
                                            >
                                                Close
                                            </CustomButton>
                                            <CustomButton
                                                disabled={
                                                    sentOtpMutation.isLoading
                                                }
                                                onClick={onSubmit}
                                                variant="contained"
                                                color="primary"
                                                className="md:w-1/2 w-full"
                                            >
                                                Get OTP
                                            </CustomButton>
                                        </div>
                                    </>
                                )}

                                {currentStep === 2 && (
                                    <>
                                        <div>
                                            <h5 className="font-bold md:text-2xl text-xl">
                                                Enter OTP
                                            </h5>
                                            <div></div>
                                            <div className="flex flex-col items-center">
                                                <h6 className="text-sm font-medium mt-1">
                                                    An OTP will be sent to{" "}
                                                    <span className="text-primary">
                                                        {mobileNumber}
                                                    </span>
                                                </h6>
                                                <CustomIconButton
                                                    onClick={
                                                        handleEditMobileNumber
                                                    }
                                                >
                                                    <Iconify
                                                        icon={IconCode.edit}
                                                    />
                                                </CustomIconButton>
                                            </div>
                                        </div>

                                        <OtpInput
                                            value={otp}
                                            onChange={(otpValue) => {
                                                setOtp(otpValue);
                                            }}
                                            numInputs={6}
                                            renderInput={(props, index) => (
                                                <input {...props} key={index} />
                                            )}
                                            inputStyle={{
                                                width: "100%",
                                                height: "3rem",
                                                margin: "0 0.5rem",
                                                fontSize: "1.5rem",
                                                borderRadius: 8,
                                                background: "#fff",
                                                border: "1px solid #bbb",
                                                color: "#000",
                                            }}
                                            isInputNum
                                            renderSeparator={<span>-</span>}
                                            pattern="[0-9]*"
                                        />

                                        <CustomButton
                                            onClick={handleOtpSubmit}
                                            variant="contained"
                                            color="primary"
                                            endIcon={
                                                isLoading && (
                                                    <Iconify
                                                        icon={
                                                            IconCode.loadingTwoTone
                                                        }
                                                    />
                                                )
                                            }
                                            disabled={isLoading}
                                        >
                                            Submit OTP
                                        </CustomButton>
                                        <div className="flex flex-row">
                                            <h6 className="text-sm font-medium">
                                                Didn't receive OTP?
                                            </h6>
                                            <h6
                                                className="text-sm text-primary font-bold ml-1 cursor-pointer"
                                                onClick={handleResendOtp}
                                            >
                                                Resend OTP
                                            </h6>
                                        </div>
                                    </>
                                )}

                                {currentStep === 3 && (
                                    <>
                                        <div className="flex flex-row items-center gap-2">
                                            <Iconify
                                                icon={
                                                    "icon-park-twotone:success"
                                                }
                                                color="success.main"
                                                size={48}
                                            />
                                            <p
                                                variant="h6"
                                                color="success.main"
                                            >
                                                You will receive a call in a few
                                                seconds.
                                            </p>
                                        </div>
                                    </>
                                )}
                            </div>
                        </form>
                    </FormProvider>
                </div>
            }
        ></ConfirmDialog>
    );
};

export default CallBackRequest;
